import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { ErpTable, Panel, Td, Th } from '@/components/erp/primitives'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { Modal } from '@/shared/ui/Modal'
import { EmptyState, ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtDateTime, fmtNumber, humanise } from '@/shared/lib/format'
import type { BatchActionReport } from '@/shared/api/types'
import {
  BATCH_KIND_LABEL, TRANSFER_STATUS_LABEL, batchMembers, closeBatch, getBatch,
  listBatches, submitBatch,
} from './api'

/**
 * Batches — a reviewable set of requests with one cause.
 *
 * The case this exists for is a **dissolved station three weeks before an
 * election**. Phase 2's merge feature publishes the event; the backend listens,
 * opens a batch and drafts one request per voter — and **submits nothing**. A
 * human opens the batch, sees how many there are, waives the consent gate with a
 * written reason where that is right, and submits.
 *
 * That is why this screen shows counters before it shows a button. Someone is
 * about to act on several thousand people at once, and the number should be in
 * front of them when they decide.
 */
export function BatchesTab() {
  const canBatch = useCan('relocation:batch')
  const [params, setParams] = useSearchParams()
  const openId = params.get('open')

  const list = useLoad(() => listBatches(), [])
  const rows = list.data ?? []

  const open = (id: string | null) => {
    const sp = new URLSearchParams(params)
    if (id) sp.set('open', id); else sp.delete('open')
    setParams(sp, { replace: true })
  }

  return (
    <>
      <ErrorStrip message={list.error} />
      <Panel>
        {list.loading && rows.length === 0 ? (
          <LoadingBlock label="Loading batches…" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No batches"
            hint="A batch appears when a station is dissolved or merged, or when someone opens one for a boundary review."
          />
        ) : (
          <ErpTable
            head={
              <>
                <Th>Reference</Th>
                <Th>Title</Th>
                <Th>Kind</Th>
                <Th>Status</Th>
                <Th className="axis-hide-md">Progress</Th>
                <Th className="axis-hide-lg">Opened</Th>
              </>
            }
          >
            {rows.map((b) => (
              <tr
                key={b.id}
                tabIndex={0}
                role="button"
                aria-label={`Open ${b.reference}`}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => open(b.id)}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(b.id) }
                }}
              >
                <Td className="font-mono text-erp-xs">{b.reference}</Td>
                <Td className="font-medium">{b.title}</Td>
                <Td>{BATCH_KIND_LABEL[b.kind] ?? humanise(b.kind)}</Td>
                <Td>{humanise(b.status)}</Td>
                <Td className="axis-hide-md">
                  {fmtNumber(b.recordedCount)} recorded · {fmtNumber(b.rejectedCount)} rejected
                  {' '}of {fmtNumber(b.requestCount)}
                </Td>
                <Td className="axis-hide-lg">{b.generatedAt ? fmtDateTime(b.generatedAt) : '—'}</Td>
              </tr>
            ))}
          </ErpTable>
        )}
      </Panel>

      {openId && (
        <BatchDrawer
          id={openId}
          canBatch={canBatch}
          onClose={() => open(null)}
          onChanged={() => { void list.reload() }}
        />
      )}
    </>
  )
}

function BatchDrawer({
  id, canBatch, onClose, onChanged,
}: {
  id: string
  canBatch: boolean
  onClose: () => void
  onChanged: () => void
}) {
  const navigate = useNavigate()
  const batch = useLoad(() => getBatch(id), [id])
  const members = useLoad(() => batchMembers(id), [id])
  const [action, setAction] = useState<'submit' | 'close' | null>(null)
  const [report, setReport] = useState<BatchActionReport | null>(null)

  const b = batch.data
  const rows = members.data?.content ?? []

  return (
    <Drawer open onClose={onClose} title={b?.title ?? 'Batch'} subtitle={b?.reference}>
      <ErrorStrip message={batch.error} />

      {b && (
        <>
          <DrawerSection title="What this is">
            <DrawerField label="Kind" value={BATCH_KIND_LABEL[b.kind] ?? humanise(b.kind)} />
            <DrawerField label="Status" value={humanise(b.status)} />
            <DrawerField label="Requests" value={fmtNumber(b.requestCount)} />
            <DrawerField label="Approved" value={fmtNumber(b.approvedCount)} />
            <DrawerField label="Recorded" value={fmtNumber(b.recordedCount)} />
            <DrawerField label="Rejected" value={fmtNumber(b.rejectedCount)} />
            {b.generatedAt && <DrawerField label="Opened" value={fmtDateTime(b.generatedAt)} />}
            {b.closedAt && <DrawerField label="Closed" value={fmtDateTime(b.closedAt)} />}
          </DrawerSection>

          {b.status === 'DRAFT' && (
            <div className="px-4 py-3">
              <Note tone="info">
                Nothing in this batch has been submitted. Drafting happens automatically; submitting
                is always a person's decision — check the members first.
              </Note>
            </div>
          )}

          {canBatch && (b.status === 'DRAFT' || b.status === 'OPEN' || b.status === 'REVIEWING') && (
            <div className="px-4 pb-3 flex flex-wrap gap-1.5">
              <button type="button" className="erp-btn erp-btn-primary" onClick={() => setAction('submit')}>
                Submit the eligible members
              </button>
              <button type="button" className="erp-btn" onClick={() => setAction('close')}>
                Close the batch
              </button>
            </div>
          )}

          {report && (
            <div className="px-4 pb-3">
              <Note tone={report.failed > 0 ? 'bad' : 'good'}>
                {fmtNumber(report.succeeded)} of {fmtNumber(report.attempted)} went through
                {report.failed > 0 && ` · ${fmtNumber(report.failed)} did not`}.
                {report.failed > 0 && ' The ones that did not are listed below with their reason — they are not lost, just not submitted.'}
              </Note>
              {report.outcomes.filter((o) => !o.ok).length > 0 && (
                <ul className="mt-2 space-y-1">
                  {report.outcomes.filter((o) => !o.ok).slice(0, 20).map((o) => (
                    <li key={o.requestId} className="text-erp-xs">
                      <code className="font-mono">{o.requestId.slice(0, 8)}</code>
                      {' — '}{o.detail ?? 'refused'}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <DrawerSection title={`Members (${fmtNumber(rows.length)}${members.data?.hasMore ? '+' : ''})`}>
            {members.loading && rows.length === 0 ? (
              <LoadingBlock label="Loading members…" />
            ) : rows.length === 0 ? (
              <p className="px-4 py-3 text-erp-sm text-muted-foreground">No members.</p>
            ) : (
              <ul className="px-4 py-2 space-y-1.5">
                {rows.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      className="text-erp-sm text-left hover:underline w-full truncate"
                      onClick={() => navigate(`/transfers/${m.id}`)}
                    >
                      <code className="font-mono text-erp-xs">{m.reference}</code>
                      <span className="text-muted-foreground ml-2">
                        {TRANSFER_STATUS_LABEL[m.status] ?? humanise(m.status)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </DrawerSection>
        </>
      )}

      {action && (
        <BatchActionModal
          id={id}
          action={action}
          onClose={() => setAction(null)}
          onDone={(r) => {
            setReport(r)
            setAction(null)
            void batch.refresh()
            void members.refresh()
            onChanged()
          }}
        />
      )}
    </Drawer>
  )
}

function BatchActionModal({
  id, action, onClose, onDone,
}: {
  id: string
  action: 'submit' | 'close'
  onClose: () => void
  onDone: (r: BatchActionReport) => void
}) {
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const closing = action === 'close'

  return (
    <Modal
      open
      onClose={onClose}
      title={closing ? 'Close this batch' : 'Submit the eligible members'}
      description={closing
        ? 'Withdraws every member that has not progressed, with your reason on each.'
        : 'Submits the members whose gates pass. The rest are reported back, not silently skipped.'}
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={saving || (closing && reason.trim().length < 5)}
            onClick={async () => {
              setSaving(true); setError(null)
              try {
                onDone(closing ? await closeBatch(id, reason.trim()) : await submitBatch(id))
              } catch (e) { setError(explain(e)); setSaving(false) }
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {closing ? 'Close' : 'Submit'}
          </button>
        </>
      }
    >
      <ErrorStrip message={error} />
      {closing ? (
        <label className="block">
          <span className="text-erp-xs text-muted-foreground">Why is this batch being closed?</span>
          <textarea className="erp-input w-full mt-1 min-h-[64px]"
            value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      ) : (
        <Note tone="info">
          Members that fail a gate stay where they are, with the failure recorded against them. You
          can waive a gate on an individual request and submit it on its own afterwards.
        </Note>
      )}
    </Modal>
  )
}
